/**
 * Core-100 R4 — CORE-N: "CORE-N SHALL recognise that some answers are time-sensitive — availability and willingness
 * change". And: "A stale start date does not follow someone into next year's application."
 *
 * Pure rules, no clock of their own (the caller passes the viewer's LOCAL date):
 *  - every time-sensitive answer is shown with the date it was stated (the row's `updated_at`), so an old answer is
 *    never presented as a current one;
 *  - an available start date that has passed is NOT current availability — it is said as a past statement.
 * No staleness period is invented: nothing here ages an answer out after N days (OD-01: never invent a period).
 */

/** The eligibility answers that change with time (availability and willingness). */
export const TIME_SENSITIVE_ELIGIBILITY_FIELDS = [
  'available_start_date',
  'willing_relocate',
  'willing_background_check',
  'willing_drug_screen',
] as const;

/** The viewer's local calendar date as YYYY-MM-DD (not the UTC date, which flips early in US evenings). */
export function localTodayIso(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export type StartDateStanding = 'unanswered' | 'current' | 'passed';

/** Whether a stated available start date still describes availability on `todayIso`. */
export function startDateStanding(startDate: string | null | undefined, todayIso: string): StartDateStanding {
  if (!startDate) return 'unanswered';
  return startDate.slice(0, 10) < todayIso.slice(0, 10) ? 'passed' : 'current';
}

/** "Stated 2026-05-01" — the as-of line for time-sensitive answers; null when the row carries no date. */
export function describeStatedOn(updatedAt: string | null | undefined): string | null {
  if (!updatedAt) return null;
  return `Stated ${updatedAt.slice(0, 10)}`;
}

/** What a read-only audience sees for the start date. A passed date is never shown as current availability. */
export function describeStartDateForViewer(startDate: string | null | undefined, todayIso: string): string {
  const standing = startDateStanding(startDate, todayIso);
  if (standing === 'unanswered') return '—';
  if (standing === 'passed') return `Not current — the stated date (${startDate!.slice(0, 10)}) has passed`;
  return startDate!.slice(0, 10);
}
